import React, { useEffect, useMemo } from 'react';
import CustomModal from 'components/custom-modal/CustomModal';
import { CustomInput, Loader } from 'components';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import COFCardDisplay from './COFCardDisplay';
import AddressSection from 'containers/invoices/components/AddressSection';
import { CustomerCard } from 'containers/customers/customers.model';
import { ParsedAddress } from 'types/baseTypes';
import { getCardType } from 'containers/receipts/util';
import { BackIcon, FormCloseIcon } from 'assets/icons';
import { nullFn } from 'constants/common';
import { useLazyGetCustomerCardByIdQuery } from 'containers/customers/api';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';

interface ManageCardModalProps {
  open: boolean;
  cardData: CustomerCard;
  onClose: () => void;
  onDeleteCard: (card: CustomerCard) => void;
  isEditable?: boolean;
}

const ManageCardModal: React.FC<ManageCardModalProps> = ({
  open,
  cardData,
  onClose,
  onDeleteCard,
  isEditable = false
}) => {
  const currentTeam = useAppSelector(selectCurrentTeam);

  const [getCustomerCardById, { isFetching: isLoadingCustomerCard, data: customerCardData }] =
    useLazyGetCustomerCardByIdQuery();

  useEffect(() => {
    if (cardData?.id) {
      getCustomerCardById({ teamId: currentTeam.id, customerId: cardData.customerId, cardId: cardData.id });
    }
  }, [cardData]);

  const handleDeleteClick = () => {
    onDeleteCard(cardData);
  };

  const initialAddress: ParsedAddress = useMemo(() => {
    if (!customerCardData?.address) {
      return {
        street: '',
        apartment: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'US',
        latitude: 0,
        longitude: 0
      };
    }

    return {
      street: customerCardData?.address.line1 || '',
      apartment: cardData.address.line2 || '',
      city: customerCardData?.address.city || '',
      state: customerCardData?.address.state || '',
      zipCode: customerCardData?.address.zipCode || '',
      country: customerCardData?.address.country || 'US',
      latitude: 0,
      longitude: 0
    };
  }, [customerCardData?.address]);

  const cardTitle = `Card on file ${cardData?.network ? `- ${getCardType(cardData?.network)}` : ''} ****${
    cardData?.cardLast4
  }`;

  return (
    <>
      <CustomModal
        open={open}
        closeModal={onClose}
        widthInPx="457px"
        className="!max-h-[92vh] min-h-[400px] p-0"
        enableOutsideClick={false}>
        <div className="relative flex h-full flex-col">
          <div className="relative flex items-start gap-2 px-6 py-5">
            <button onClick={onClose} className="flex items-center justify-center" aria-label="Go back" tabIndex={0}>
              <BackIcon className="path-stroke-current h-5 w-5 text-primaryText" />
            </button>
            <div className="space-y-1">
              <h2 className="text-17px font-bold text-heading">{cardTitle}</h2>
              {customerCardData && (
                <p className="text-sbase font-medium text-heading">{customerCardData.customerName}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="absolute right-6 top-6 flex cursor-pointer items-center justify-center"
              aria-label="Close modal"
              tabIndex={0}>
              <FormCloseIcon className="h-6 w-6" />
            </button>
          </div>
          {isLoadingCustomerCard && (
            <div className="flex items-center justify-center py-40">
              <Loader />
            </div>
          )}
          {!isLoadingCustomerCard && customerCardData && (
            <div className="flex-1 overflow-y-auto px-6 ">
              <div className="space-y-5">
                <p className="text-sbase text-primaryText">
                  Make sure your customer has given you explicit consent to save their card for payments. Keep a copy of
                  their signed approval to protect yourself against disputes and chargebacks.
                </p>
                <div className="space-y-4">
                  <div>
                    <div className="mb-2.5 text-sbase font-bold text-primaryText">
                      Card information <span className="text-error">*</span>
                    </div>
                    <div className="rounded border border-borderGray bg-secondaryBtn p-3 opacity-70">
                      <COFCardDisplay cardData={customerCardData} showCardHolderName={false} />
                    </div>
                  </div>

                  <CustomInput
                    id="cardholder-name"
                    label="Cardholder's name"
                    value={customerCardData?.cardHolderName || ''}
                    onChange={nullFn}
                    disabled={!isEditable}
                    placeholder="Cardholder name"
                    isMandatory={true}
                    className={`${!isEditable ? 'opacity-70' : ''} border border-borderGray`}
                  />
                  <CustomInput
                    id="cardholder-email"
                    label="Cardholder's email"
                    value={customerCardData?.email || ''}
                    onChange={nullFn}
                    disabled={!isEditable}
                    placeholder="Cardholder's email"
                    className={`${!isEditable ? 'opacity-70' : ''} border border-borderGray`}
                  />
                </div>

                <div className="space-y-4">
                  <div className="text-sbase font-bold text-primaryText">Billing address</div>
                  <AddressSection
                    onAddressChange={nullFn}
                    initialAddress={initialAddress}
                    isDisabled={!isEditable}
                    inputClassName={`${!isEditable ? 'opacity-70' : ''} border border-borderGray`}
                  />
                </div>

                <div className="pb-6">
                  <CustomButton
                    id="delete-card-on-file-btn"
                    onClick={handleDeleteClick}
                    type={ButtonType.SECONDARY}
                    className="!h-[50px] w-full !border-error !text-error hover:!bg-error hover:!bg-opacity-5">
                    Delete card on file
                  </CustomButton>
                </div>
              </div>
            </div>
          )}
        </div>
      </CustomModal>
    </>
  );
};

export default ManageCardModal;
